import { useEffect, useState } from "react";
import { BadgePercent, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR } from "../../lib/api";
import { PageHeader, LoadingRows, EmptyState } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";

const KIND_LABEL = { store_product: "Store Product", mkt_service: "Service", mkt_product: "Digital Product", mkt_course: "Course" };
const PCT_PRESETS = [5, 10, 15, 20, 30];
const FIXED_PRESETS = [500, 1000, 2000];

export default function OwnerAffiliates() {
  const [items, setItems] = useState(null);
  const [offers, setOffers] = useState(null);
  const [dlg, setDlg] = useState(null); // item being configured
  const [ctype, setCtype] = useState("pct");
  const [cval, setCval] = useState("10");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/vid/owner/items").then((r) => setItems(r.data.items)).catch((e) => { toast.error(errMsg(e)); setItems([]); });
    api.get("/vid/owner/offers").then((r) => setOffers(r.data.offers)).catch(() => setOffers([]));
  };
  useEffect(() => { load(); }, []);

  const enable = async () => {
    if (!cval || Number(cval) <= 0) { toast.error("Enter commission"); return; }
    setBusy(true);
    try {
      await api.post("/vid/owner/offers", { kind: dlg.kind, ref_id: dlg.ref_id, commission_type: ctype, commission_value: Number(cval) });
      toast.success("Affiliate program enabled!"); setDlg(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const toggle = async (o, active) => {
    try { await api.patch(`/vid/owner/offers/${o.id}`, { active }); toast.success(active ? "Offer enabled" : "Offer disabled"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const offerMap = Object.fromEntries((offers || []).map((o) => [`${o.kind}:${o.ref_id}`, o]));

  return (
    <div data-testid="owner-affiliates-page">
      <PageHeader title="Affiliate Program" subtitle="Let creators promote your products & services — you control the commission" testid="affiliates-title" />

      {!items || !offers ? <LoadingRows rows={4} /> : items.length === 0 ? (
        <EmptyState title="No sellable items" body="Create a product or service first, then enable its affiliate program here." icon={BadgePercent} />
      ) : (
        <div className="space-y-3">
          {items.map((it) => {
            const off = offerMap[`${it.kind}:${it.ref_id}`];
            return (
              <div key={`${it.kind}-${it.ref_id}`} data-testid={`aff-item-${it.ref_id}`} className="bg-card border border-border rounded-xl p-4 flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-display font-bold text-sm">{it.name}</p>
                    <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{KIND_LABEL[it.kind]}</span>
                    {off && <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${off.active ? "bg-emerald-500/10 text-emerald-600" : "bg-zinc-500/10 text-zinc-500"}`}>
                      {off.active ? `Affiliates ON · ${off.commission_type === "pct" ? `${off.commission_value}%` : `₹${off.commission_value}`}` : "Affiliates OFF"}</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{it.store} · {it.category || "—"} · {fmtINR(it.price)}</p>
                </div>
                <div className="flex items-center gap-3">
                  {off && <Switch data-testid={`aff-toggle-${it.ref_id}`} checked={off.active} onCheckedChange={(v) => toggle(off, v)} />}
                  <Button size="sm" variant={off ? "outline" : "default"} data-testid={`aff-configure-${it.ref_id}`}
                    onClick={() => { setDlg(it); setCtype(off?.commission_type || "pct"); setCval(String(off?.commission_value || "10")); }}
                    className={`rounded-full font-bold ${off ? "" : "gradient-bg text-white border-0"}`}>
                    {off ? "Edit Commission" : "Allow Affiliates"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="aff-offer-dialog">
          <DialogHeader><DialogTitle className="font-display">Affiliate Commission — {dlg?.name}</DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Price: <b>{fmtINR(dlg.price)}</b></p>
              <div className="grid grid-cols-2 gap-2 p-1 rounded-full bg-muted">
                <button data-testid="aff-type-pct" onClick={() => setCtype("pct")}
                  className={`py-2 rounded-full text-xs font-bold ${ctype === "pct" ? "gradient-bg text-white" : "text-muted-foreground"}`}>Percentage %</button>
                <button data-testid="aff-type-fixed" onClick={() => setCtype("fixed")}
                  className={`py-2 rounded-full text-xs font-bold ${ctype === "fixed" ? "gradient-bg text-white" : "text-muted-foreground"}`}>Fixed ₹</button>
              </div>
              <div className="flex flex-wrap gap-2">
                {(ctype === "pct" ? PCT_PRESETS : FIXED_PRESETS).map((p) => (
                  <button key={p} data-testid={`aff-preset-${p}`} onClick={() => setCval(String(p))}
                    className={`text-xs font-bold px-4 py-2 rounded-full ${String(p) === cval ? "gradient-bg text-white" : "border border-border"}`}>
                    {ctype === "pct" ? `${p}%` : `₹${p}`}
                  </button>
                ))}
              </div>
              <div className="space-y-1.5">
                <Label>{ctype === "pct" ? "Custom %" : "Custom amount (₹)"}</Label>
                <Input data-testid="aff-custom-value" type="number" min="1" value={cval} onChange={(e) => setCval(e.target.value)} />
              </div>
              {cval && Number(cval) > 0 && (
                <p className="text-xs font-bold text-emerald-600 dark:text-emerald-400" data-testid="aff-preview">
                  Creator earns {ctype === "pct" ? fmtINR(Math.round(dlg.price * Number(cval) / 100)) : fmtINR(Number(cval))} per {fmtINR(dlg.price)} sale
                </p>
              )}
              <Button data-testid="aff-save" onClick={enable} disabled={busy} className="w-full h-11 rounded-full font-bold gradient-bg text-white border-0">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Affiliate Program"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
